import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { isAuthenticatedAtom } from '@/store';
import { apiClient } from '@/api/apiClient';

// REQ-PORTAL-0001-4e184: Authenticated Keycloak users without portal registration have an in-app recovery path.
// REQ-PORTAL-0002-93147: Portal registration flow supports linking an existing authenticated Keycloak identity.
//
// Returns the caller's linkage state to the portal-specific user table:
//   - 'loading'  — request in flight.
//   - 'linked'   — Keycloak sub has a portal-user row; the app should render as normal.
//   - 'unlinked' — Keycloak sub has NO portal-user row; the app should route the user through
//                  AuthenticatedRegistrationRoute → the wizard so they can self-heal.
//   - 'unknown'  — either the user is not authenticated, or the request failed for any reason
//                  other than "backend said unlinked" (network error, 5xx, endpoint absent). Callers
//                  should treat 'unknown' the same as 'unlinked' from a routing standpoint so the
//                  user is never trapped, but MUST NOT mutate anything based on 'unknown' alone.
//
// Graceful degradation: during Phase 1 rollout the /session/registration-status endpoint may not yet
// be deployed. Any non-2xx response (including 404) resolves to 'unlinked' — the hook is intentionally
// permissive so a partial deployment never bricks the portal for signed-in users.

export type PortalRegistrationStatus = 'linked' | 'unlinked' | 'unknown' | 'loading';

interface RegistrationStatusResponse {
  linked: boolean;
}

export const usePortalRegistrationStatus = (): PortalRegistrationStatus => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const [status, setStatus] = useState<PortalRegistrationStatus>('loading');

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus('unknown');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const resp = await apiClient.get<RegistrationStatusResponse>(
          '/session/registration-status'
        );
        if (cancelled) return;
        setStatus(resp?.linked === true ? 'linked' : 'unlinked');
      } catch {
        // Graceful degradation: the endpoint may not yet exist during Phase 1 rollout, or the
        // service may be transiently unhealthy. Fall back to 'unlinked' so the user can still
        // reach the registration wizard rather than being trapped on the dashboard error card.
        if (cancelled) return;
        setStatus('unlinked');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return status;
};
